import React, { useContext, memo, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';

import { createUseStyles, Select, Icon } from '@Sage/hoi-poi-ui';
import { useWeb5Context } from 'lib/Web5Context';
import ConditionsContext from './ConditionsContext';
import FieldValue from './FieldValue';

import styles from './styles/condition';
const useStyles = createUseStyles(styles, 'ConditionsCondition');

const operators = {
    '==': 'cfm_label_equals',
    '!=': 'cfm_label_not_equals',
    '>': 'cfm_label_gth',
    '>=': 'cfm_label_gth_or_eq',
    '<': 'cfm_label_lth',
    '<=': 'cfm_label_lth_or_eq',
};

const Condition = ({ condition, index, onChange, onRemove, options }) => {
    const { t } = useWeb5Context();
    const classes = useStyles();
    const { fields, isReadOnly } = useContext(ConditionsContext);
    const fieldOptions = useMemo(
        () =>
            fields.map((f) => {
                const { label, value, isExtra } = f;
                return {
                    label: isExtra ? label : t(label),
                    value,
                };
            }),
        [fields, t],
    );

    const operatorOptions = useMemo(() => {
        if (!fields || !condition.field) return [];
        const foundField = fields.find(
            (x) => x.value === (condition?.field?.value || condition?.field),
        );
        if (!foundField?.conditions) return;
        return foundField.conditions
            .filter((c) => operators[c] !== undefined)
            .map((cond) => ({
                label: t(operators[cond]),
                value: cond,
            }));
    }, [condition, fields, t]);

    const fieldSchema = useMemo(() => {
        if (!fields || fields.length <= 0) return {};
        return fields.find((x) => x.value === (condition?.field?.value || condition?.field)) || {};
    }, [condition, fields]);

    const handleFieldChange = useCallback(
        (val) => {
            onChange(index, {
                field: val,
                operator: null,
                value: null,
            });
        },
        [index, onChange],
    );

    const handleOperatorChange = useCallback(
        (val) => {
            let newValues = { operator: val };
            if (!val) newValues.value = null;
            onChange(index, newValues);
        },
        [index, onChange],
    );

    const handleValueChange = useCallback(
        (e) => {
            let value = e?.target?.value || e;
            switch (fieldSchema?.type) {
                case 'currency':
                case 'int':
                    value = !isNaN(value) ? parseInt(value, 10) : value;
                    break;
                case 'decimal':
                    value = !isNaN(value) ? parseFloat(value) : value;
                    break;
                default:
                    break;
            }
            onChange(index, {
                value,
            });
        },
        [fieldSchema, index, onChange],
    );

    const handleRemoveCondition = useCallback(() => {
        onRemove(index);
    }, [index, onRemove]);

    const fieldReadOnly = useMemo(
        () => !condition.field || isReadOnly,
        [condition.field, isReadOnly],
    );

    const valueReadOnly = useMemo(
        () => !(condition.field && condition.operator) || isReadOnly,
        [condition.field, condition.operator, isReadOnly],
    );

    const conditionFieldClassNames = classnames(
        classes.field,
        'conditions__condition__select__field',
    );
    const conditionOperatorClassNames = classnames(
        classes.field,
        'conditions__condition__select__operator',
    );
    const conditionValueClassNames = classnames(
        classes.field,
        'conditions__condition__field-value',
        {
            'conditions__condition__field-value--disabled': valueReadOnly,
        },
    );
    const conditionDeleteClassNames = classnames(
        classes.trashIcon,
        'conditions__condition__delete-condition-button',
    );

    const field =
        condition.field && !condition.field?.value
            ? fieldOptions?.find((op) => op.value === condition.field)
            : condition.field;

    const operator =
        condition.operator && !condition.operator?.value
            ? operatorOptions?.find((op) => op.value === condition.operator)
            : condition.operator;

    return (
        <div className={classes.root}>
            <Select
                className={conditionFieldClassNames}
                value={field}
                options={fieldOptions}
                onChange={handleFieldChange}
                isReadOnly={isReadOnly}
                isFullWidth
            />
            <Select
                className={conditionOperatorClassNames}
                value={operator}
                options={operatorOptions}
                onChange={handleOperatorChange}
                isReadOnly={fieldReadOnly}
                isFullWidth
            />
            <FieldValue
                className={conditionValueClassNames}
                value={condition.value}
                onChange={handleValueChange}
                isReadOnly={valueReadOnly}
                type={fieldSchema.type || 'text'}
                table={fieldSchema.table}
                options={options}
                selectedField={condition.field}
            />
            {!isReadOnly && (
                <Icon
                    className={conditionDeleteClassNames}
                    name="trash"
                    onClick={handleRemoveCondition}
                />
            )}
        </div>
    );
};

Condition.propTypes = {
    condition: PropTypes.shape({
        operator: PropTypes.string,
        field: PropTypes.string,
        value: PropTypes.oneOfType([
            PropTypes.number,
            PropTypes.string,
            PropTypes.arrayOf(PropTypes.any),
            PropTypes.bool,
            PropTypes.object,
        ]),
    }).isRequired,
    index: PropTypes.number.isRequired,
    onChange: PropTypes.func.isRequired,
    onRemove: PropTypes.func.isRequired,
};

export default memo(Condition);
